import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  CalendarIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { format } from 'date-fns'

import { getRepeatDescription } from '../repeating-events/RepeatDescription'
import { updateCalendarEvent, updateRecurringEvent } from '../../lib/db'
import { EventReminders } from './EventReminders'
import { EventReminderConfig } from '../../lib/db/db.types'
import { LottieCelebration } from '../lottie/LottieCelebration'
import { useAuth } from '../../store/hooks'
import {
  CalendarEvent as CalendarEventType,
  RepeatingEvent,
} from '../../store/slices/slices.types'
import { setCurrentWorkspace } from '../../store/slices/workspaceSlice'

export function CalendarEvent({
  event,
  repeatingEvent,
  collaborators,
}: {
  event: CalendarEventType
  repeatingEvent?: RepeatingEvent
  collaborators: Array<{
    id: string
    name?: string
    email: string
  }>
}) {
  const dispatch = useDispatch()
  const { authUser } = useAuth()

  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [notes, setNotes] = useState(event?.notes || '')
  const [isSaving, setIsSaving] = useState(false)
  const [newAction, setNewAction] = useState('')
  const [isAddingAction, setIsAddingAction] = useState(false)
  const [showCelebration, setShowCelebration] = useState(false)

  const handleSaveNotes = async () => {
    if (!event || !authUser) return
    setIsSaving(true)
    try {
      await updateCalendarEvent({
        workspaceId: event.workspaceId,
        eventId: event.id,
        updates: {
          notes,
        },
      })
      setIsEditingNotes(false)
    } catch (error) {
      console.error('Failed to save notes:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddAction = async () => {
    if (!event || !authUser || !newAction.trim()) return

    const newActions = [
      ...(event.actions || []),
      {
        id: crypto.randomUUID(), // Generate a unique ID
        description: newAction.trim(),
        completed: false,
      },
    ]

    try {
      await updateCalendarEvent({
        workspaceId: event.workspaceId,
        eventId: event.id,
        updates: {
          actions: newActions,
        },
      })
      setNewAction('')
      setIsAddingAction(false)
    } catch (error) {
      console.error('Failed to add action:', error)
    }
  }

  const handleUpdateReminders = async (reminders: EventReminderConfig) => {
    if (!authUser) return
    if (!repeatingEvent) return

    await updateRecurringEvent({
      workspaceId: event.workspaceId,
      eventId: repeatingEvent.id,
      updates: {
        reminders,
      },
    })
  }

  const handleToggleComplete = async () => {
    if (!event || !authUser) return
    try {
      const newValue = !event.isCompleted
      await updateCalendarEvent({
        workspaceId: event.workspaceId,
        eventId: event.id,
        updates: {
          isCompleted: newValue,
        },
      })

      if (newValue) {
        setShowCelebration(true)
      }
    } catch (error) {
      console.error('Failed to toggle completion:', error)
    }
  }

  const handleDeleteAction = async (actionId: string) => {
    if (!event || !authUser) return
    await updateCalendarEvent({
      workspaceId: event.workspaceId,
      eventId: event.id,
      updates: {
        actions: event.actions?.filter((a) => a.id !== actionId) || [],
      },
    })
  }

  const handleMarkActionComplete = async (
    actionId: string,
    completed: boolean,
  ) => {
    if (!event || !authUser) return
    await updateCalendarEvent({
      workspaceId: event.workspaceId,
      eventId: event.id,
      updates: {
        actions:
          event.actions?.map((a) =>
            a.id === actionId ? { ...a, completed } : a,
          ) || [],
      },
    })
  }

  useEffect(() => {
    if (event.workspaceId) {
      dispatch(setCurrentWorkspace({ id: event.workspaceId }))
    }
  }, [dispatch, event.workspaceId])

  if (!event) return null

  return (
    <>
      <div className="space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <div className="flex items-start justify-between">
            <div>
              <h1
                className={`text-2xl font-bold flex items-center ${
                  event.isCompleted
                    ? 'text-grey-300 line-through'
                    : 'text-gray-900'
                }`}
              >
                {event.name}
              </h1>
              <div className="mt-3 flex items-center text-sm text-gray-500">
                <CalendarIcon className="h-4 w-4 mr-1" />
                {format(new Date(event.date), 'EEEE MMMM dd, yyyy')}
              </div>
              {repeatingEvent && (
                <div className="mt-2">
                  <dd className="text-sm text-gray-400">
                    <span className="font-medium">Repeats:</span>{' '}
                    {getRepeatDescription(repeatingEvent.repeats)}
                  </dd>
                  {repeatingEvent.repeats.end && (
                    <dd className="mt-1 text-sm text-gray-500">
                      Until{' '}
                      {format(
                        new Date(repeatingEvent.repeats.end),
                        'MMMM d, yyyy',
                      )}
                    </dd>
                  )}
                  {!!repeatingEvent.repeats.exceptions &&
                    repeatingEvent.repeats.exceptions.length > 0 && (
                      <div className="px-6 py-5 border-t border-gray-200">
                        <h2 className="text-sm font-medium text-gray-500">
                          Exceptions
                        </h2>
                        <ul className="mt-2 divide-y divide-gray-200">
                          {repeatingEvent.repeats.exceptions.map((date) => (
                            <li
                              key={date}
                              className="py-2 text-sm text-gray-900"
                            >
                              {format(new Date(date), 'EEEE, MMMM d, yyyy')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              )}
            </div>
            <button
              onClick={handleToggleComplete}
              className={`px-4 py-3 rounded-md font-medium text-md inline-flex items-center ${
                event.isCompleted
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {!event.isCompleted && (
                <CheckCircleIcon className="h-5 w-5 mr-2" />
              )}
              {event.isCompleted ? 'Mark as Incomplete' : 'Mark as Complete'}
            </button>
          </div>
        </div>

        <div className="px-6 py-5 bg-gray-50 rounded-lg space-y-8">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Event Details
            </h2>
            <p className="text-gray-600 mt-1">
              Add notes and actions to help you manage this event.
            </p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700">Notes</div>
              {!isEditingNotes && (
                <button
                  onClick={() => setIsEditingNotes(true)}
                  className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
              )}
            </div>
            {isEditingNotes ? (
              <div className="mt-1 space-y-2">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Add notes about this event..."
                />
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setIsEditingNotes(false)
                      setNotes(event.notes || '')
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    disabled={isSaving}
                  >
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNotes}
                    disabled={isSaving}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <CheckIcon className="h-4 w-4 mr-1" />
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {notes || 'No notes added yet.'}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700">Actions</div>
              <button
                onClick={() => setIsAddingAction(true)}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Action
              </button>
            </div>
            <div className="space-y-2">
              {event.actions?.map((action, index) => (
                <div key={action.id} className="flex items-center gap-2 group">
                  <input
                    type="checkbox"
                    checked={action.completed}
                    onChange={() =>
                      handleMarkActionComplete(action.id, !action.completed)
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span
                    className={`flex-1 text-sm ${
                      action.completed
                        ? 'text-gray-500 line-through'
                        : 'text-gray-900'
                    }`}
                  >
                    {action.description}
                  </span>
                  <button
                    onClick={() => handleDeleteAction(action.id)}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {isAddingAction && (
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={newAction}
                      onChange={(e) => setNewAction(e.target.value)}
                      placeholder="Enter new action..."
                      className="block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddAction()
                        }
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddAction}
                      disabled={!newAction.trim()}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setIsAddingAction(false)
                        setNewAction('')
                      }}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {(!event.actions || event.actions.length === 0) &&
                !isAddingAction && (
                  <p className="text-sm text-gray-500">No actions added yet.</p>
                )}
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <EventReminders
            eventDate={event.date}
            reminders={repeatingEvent?.reminders}
            onUpdate={(cfg) => handleUpdateReminders(cfg)}
            users={collaborators}
          />
        </div>
      </div>
      {showCelebration && (
        <LottieCelebration
          type="success"
          message={
            <div className="text-blue-500 text-center">
              <p className="text-lg font-medium">Great job!</p>
              <p className="text-2xl font-bold">{event.name} completed!</p>
            </div>
          }
          onComplete={() => setShowCelebration(false)}
        />
      )}
    </>
  )
}
